"use client";

import { ReactNode, useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import { Zap, Mail, Phone, MapPin, Facebook, Twitter, Linkedin, Menu, X } from "lucide-react";
import CookieConsent from "@/components/ui/CookieConsent";
import { useAuth } from "@/lib/useAuth";

export default function PublicLayout({ children }: { children: ReactNode }) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const router = useRouter();
  const { user, isLoading, logout } = useAuth();

  // Capture referral code on any public page with ?ref=CODE
  useEffect(() => {
    const captureReferralCode = async () => {
      const refCode = new URLSearchParams(window.location.search).get("ref");
      if (!refCode) return;

      try {
        const { captureReferralCode: persistCode } = await import("@/lib/referral-persistence");
        const { storeTrackingParams } = await import("@/lib/referral-tracking");

        // Store in localStorage and sessionStorage
        persistCode(refCode);
        storeTrackingParams({ referralCode: refCode });

        console.log("Referral code captured on public page:", refCode);
      } catch (err) {
        console.error("Failed to capture referral code:", err);
      }
    };

    captureReferralCode();
  }, []);

  const getDashboardLink = () => {
    if (!user) return "/";
    if (user.role === "ADMIN" || user.role === "SUPER_ADMIN") return "/admin";
    if (user.hasReferral) return "/referrals";
    if (user.role === "GARAGE_STAFF") return "/garage/jobs";
    if (user.hasGarage) return "/garage/dashboard";
    return "/";
  };

  const handleLogout = async () => {
    await logout();
    router.push("/");
    setIsMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <CookieConsent />
      {/* Header */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 md:py-4 flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2 min-w-0">
            <Image
              src="/logo.png"
              alt="Garage Boost"
              width={180}
              height={52}
              className="h-9 md:h-11 w-auto object-contain"
              priority
            />
          </Link>
          
          <div className="hidden lg:flex items-center gap-6">
            <Link href="/" className="text-sm font-semibold text-slate-600 hover:text-blue-600 transition-colors">
              Home
            </Link>
            <Link href="/features" className="text-sm font-semibold text-slate-600 hover:text-blue-600 transition-colors">
              Features
            </Link>
            <Link href="/pricing" className="text-sm font-semibold text-slate-600 hover:text-blue-600 transition-colors">
              Pricing
            </Link>
            <Link href="/garages" className="text-sm font-semibold text-slate-600 hover:text-blue-600 transition-colors">
              Find Garage
            </Link>
            <Link href="/join-referrals" className="text-sm font-semibold text-slate-600 hover:text-blue-600 transition-colors">
              Referral Program
            </Link>
            <Link href="/blog" className="text-sm font-semibold text-slate-600 hover:text-blue-600 transition-colors">
              Blog
            </Link>
            <Link href="/contact" className="text-sm font-semibold text-slate-600 hover:text-blue-600 transition-colors">
              Contact
            </Link>
          </div>

          <div className="hidden lg:flex items-center gap-2 md:gap-3">
            {isLoading ? (
              <div className="w-20 h-10 bg-slate-200 rounded-lg animate-pulse" />
            ) : user ? (
              <>
                <Link
                  href={getDashboardLink()}
                  className="text-sm font-semibold text-slate-700 hover:text-blue-600 touch-manipulation px-4 py-2 transition-colors"
                >
                  Dashboard
                </Link>
                <button
                  onClick={handleLogout}
                  className="text-sm font-bold bg-linear-to-r from-red-600 to-red-700 text-white px-6 py-2.5 rounded-lg hover:from-red-700 hover:to-red-800 shadow-lg shadow-red-500/30 hover:shadow-red-500/50 whitespace-nowrap touch-manipulation min-h-11 flex items-center transition-all"
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link
                  href="/login"
                  className="text-sm font-semibold text-slate-700 hover:text-blue-600 touch-manipulation px-4 py-2 transition-colors"
                >
                  Login
                </Link>
                <Link
                  href="/signup"
                  className="text-sm font-bold bg-linear-to-r from-blue-600 to-indigo-600 text-white px-6 py-2.5 rounded-lg hover:from-blue-700 hover:to-indigo-700 shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 whitespace-nowrap touch-manipulation min-h-11 flex items-center transition-all"
                >
                  Start Free Trial
                </Link>
              </>
            )}
          </div>

          <button
            type="button"
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMobileMenuOpen}
            onClick={() => setIsMobileMenuOpen((prev) => !prev)}
            className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg border border-slate-200 text-slate-700 hover:text-blue-600 hover:border-blue-200"
          >
            {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {isMobileMenuOpen && (
          <div className="lg:hidden border-t border-slate-200 bg-white shadow-lg">
            <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-1">
              <Link href="/" className="px-4 py-3 text-sm font-semibold text-slate-700 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all" onClick={() => setIsMobileMenuOpen(false)}>
                Home
              </Link>
              <Link href="/features" className="px-4 py-3 text-sm font-semibold text-slate-700 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all" onClick={() => setIsMobileMenuOpen(false)}>
                Features
              </Link>
              <Link href="/pricing" className="px-4 py-3 text-sm font-semibold text-slate-700 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all" onClick={() => setIsMobileMenuOpen(false)}>
                Pricing
              </Link>
              <Link href="/garages" className="px-4 py-3 text-sm font-semibold text-slate-700 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all" onClick={() => setIsMobileMenuOpen(false)}>
                Find Garage
              </Link>
              <Link href="/join-referrals" className="px-4 py-3 text-sm font-semibold text-slate-700 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all" onClick={() => setIsMobileMenuOpen(false)}>
                Referral Program
              </Link>
              <Link href="/blog" className="px-4 py-3 text-sm font-semibold text-slate-700 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all" onClick={() => setIsMobileMenuOpen(false)}>
                Blog
              </Link>
              <Link href="/contact" className="px-4 py-3 text-sm font-semibold text-slate-700 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all" onClick={() => setIsMobileMenuOpen(false)}>
                Contact
              </Link>

              <div className="mt-3 pt-4 border-t border-slate-200 flex flex-col gap-3">
                {isLoading ? (
                  <div className="w-full h-10 bg-slate-200 rounded-lg animate-pulse" />
                ) : user ? (
                  <>
                    <Link
                      href={getDashboardLink()}
                      className="w-full text-center text-sm font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg py-3 transition-all"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Dashboard
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="w-full text-center text-sm font-bold bg-linear-to-r from-red-600 to-red-700 text-white rounded-lg py-3 shadow-lg shadow-red-500/30 transition-all"
                    >
                      Logout
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      href="/login"
                      className="w-full text-center text-sm font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg py-3 transition-all"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Login
                    </Link>
                    <Link
                      href="/signup"
                      className="w-full text-center text-sm font-bold bg-linear-to-r from-blue-600 to-indigo-600 text-white rounded-lg py-3 shadow-lg shadow-blue-500/30 transition-all"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Start Free Trial
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-4 gap-12">
          <div>
            <Link href="/" className="flex items-center gap-2 mb-4">
              <Image
                src="/logo-white.png"
                alt="Garage Boost"
                width={190}
                height={56}
                className="h-10 md:h-11 w-auto object-contain"
              />
            </Link>
            <p className="text-sm text-slate-400 leading-relaxed">
              Complete garage management platform for UK MOT garages and automotive workshops.
            </p>
            <div className="flex items-center gap-3 mt-6">
              <a href="https://www.facebook.com/BizzBoost.uk" target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-slate-800 rounded-lg flex items-center justify-center hover:bg-blue-600 transition-colors">
                <Facebook size={18} />
              </a>
              <a href="https://x.com/BizzBoostUK" target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-slate-800 rounded-lg flex items-center justify-center hover:bg-blue-600 transition-colors">
                <Twitter size={18} />
              </a>
              <a href="https://www.linkedin.com/company/bizz-boost/" target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-slate-800 rounded-lg flex items-center justify-center hover:bg-blue-600 transition-colors">
                <Linkedin size={18} />
              </a>
            </div>
          </div>

          <div>
            <h3 className="font-bold text-white mb-4">Product</h3>
            <ul className="space-y-3 text-sm text-slate-400">
              <li><Link href="/features" className="hover:text-white transition-colors">Features</Link></li>
              <li><Link href="/pricing" className="hover:text-white transition-colors">Pricing</Link></li>
              <li><Link href="/garages" className="hover:text-white transition-colors">Find Garage</Link></li>
              <li><Link href="/signup" className="hover:text-white transition-colors">Start Free Trial</Link></li>
            </ul>
          </div>

          <div>
            <h3 className="font-bold text-white mb-4">Company</h3>
            <ul className="space-y-3 text-sm text-slate-400">
              <li><Link href="/contact" className="hover:text-white transition-colors">Contact</Link></li>
              <li><Link href="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
              <li><Link href="/terms" className="hover:text-white transition-colors">Terms of Service</Link></li>
            </ul>
          </div>

          <div>
            <h3 className="font-bold text-white mb-4">Contact</h3>
            <ul className="space-y-3 text-sm text-slate-400">
              <li className="flex items-start gap-2">
                <Mail size={16} className="mt-0.5 shrink-0" />
                <a href="mailto:cs@bizzboost.uk" className="hover:text-white transition-colors">cs@bizzboost.uk</a>
              </li>
              <li className="flex items-start gap-2">
                <Phone size={16} className="mt-0.5 shrink-0" />
                <a href="tel:07380448187" className="hover:text-white transition-colors">07380 448187</a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin size={16} className="mt-0.5 shrink-0" />
                <span>Great Portland St, London W1W 5PF</span>
              </li>
            </ul>
            
            <div className="mt-6 pt-6 border-t border-slate-800">
              <h4 className="font-bold text-white mb-2 text-sm">💰 Referral Program</h4>
              <p className="text-xs text-slate-400 mb-3">Refer garages and earn commission!</p>
              <Link
                href="/join-referrals"
                className="inline-block text-xs font-semibold bg-linear-to-r from-blue-600 to-indigo-600 text-white px-4 py-2 rounded-lg hover:from-blue-700 hover:to-indigo-700 shadow-lg shadow-blue-500/30 transition-all"
              >
                Join Now →
              </Link>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 mt-12 pt-8 border-t border-slate-800">
          <p suppressHydrationWarning className="text-sm text-slate-500 text-center">
            © {new Date().getFullYear()} Garage Boost. All rights reserved. Built for UK garages.
          </p>
        </div>
      </footer>
    </div>
  );
}
